import { storyThemeIdOf } from './storyFrames';
import { coverFrameHeight } from './storyLayout';
import { mixHex, storyAlpha, type StoryColours, type StoryCss } from './storyStyles';
import { round3 } from './videoMotion';

/**
 * The Spotify Canvas loop (design `asset-redesign/s Canvas loop - new`, owner answers 2026-10-08):
 * the whole cover in its theme frame in the free window between the Spotify top bar and the player,
 * one loop of exactly four bars (3 to 8 s, whole bars). Three motions only, each with the loop as
 * its period, so the last frame joins frame 0 with no jump: a kick on each downbeat, a slow float
 * and one theme motion. Nothing moves under the player (y 1114 on), and the ground darkens there.
 * No text, no logo, no link; on Free the watermark lockup (a row, L 44, `canvasWatermark` of
 * `watermark.ts`, design w0) under the cover stage, from y 1038, on every frame.
 */

/** The texts and the loop of one Canvas (`plan.short`, kind `canvas`). */
export interface KitCanvasVideo {
  kind: 'canvas';
  /** The bars of the loop (4 when they fit 3 to 8 s). */
  bars: number;
  /** The length of the loop in frames (30 fps): every motion has this period. */
  loopFrames: number;
  /** The tile letters of a song with no artwork (the title). */
  title: string;
  /** The poster: frame 0. */
  posterSec: number;
}

/** The free window between the Spotify top bar and the player (estimates from the iOS app). */
export const CANVAS_FREE_TOP = 230;
export const CANVAS_PLAYER_TOP = 1114;
/** The centre of the stage of the cover (design s: the stage y 300 to 1020). */
export const CANVAS_STAGE_CENTRE = { x: 540, y: 660 };

const MIN_LOOP_SEC = 3;
const MAX_LOOP_SEC = 8;
/** The bars of the loop when they fit: four (owner answer 2 of page s). */
const LOOP_BARS = 4;

/**
 * The bars of a Canvas loop for a bar of `barSec`: four when four bars last 3 to 8 s; else the most
 * whole bars up to 8 s (at least 3 s, so a fast song gets more). With no beat grid (`barSec` null),
 * 8 s in four phrases. Pure.
 */
export function canvasLoopBars(barSec: number | null): { bars: number; loopSec: number } {
  if (barSec === null || !Number.isFinite(barSec) || barSec <= 0) {
    return { bars: LOOP_BARS, loopSec: MAX_LOOP_SEC };
  }
  if (LOOP_BARS * barSec >= MIN_LOOP_SEC && LOOP_BARS * barSec <= MAX_LOOP_SEC) {
    return { bars: LOOP_BARS, loopSec: round3(LOOP_BARS * barSec) };
  }
  const most = Math.max(1, Math.floor(MAX_LOOP_SEC / barSec + 1e-9));
  if (most * barSec >= MIN_LOOP_SEC || most === 1) {
    return { bars: most, loopSec: round3(Math.min(MAX_LOOP_SEC, most * barSec)) };
  }
  return { bars: most, loopSec: round3(most * barSec) };
}

/** The bar length of a song: the median gap of its downbeats, else 4 beats at its tempo. Null: no grid. Pure. */
export function songBarSec(analysis: {
  bpm: number;
  downbeats: number[];
  beatGrid?: 'detected' | 'fallback';
}): number | null {
  if (analysis.beatGrid === 'fallback') {
    return null;
  }
  const gaps = analysis.downbeats
    .slice(1)
    .map((beat, index) => beat - (analysis.downbeats[index] ?? beat))
    .filter((gap) => gap > 0)
    .sort((a, b) => a - b);
  const median = gaps[Math.floor(gaps.length / 2)];
  if (median !== undefined) {
    return median;
  }
  return analysis.bpm > 0 ? (4 * 60) / analysis.bpm : null;
}

/** The cover of a theme on the stage: 680 px (Hype 640, Retro 600 moved left, Handmade a 600 px photo). */
export function canvasCoverBox(themeId: string): { x: number; y: number; w: number; h: number } {
  const id = storyThemeIdOf(themeId);
  const width = id === 'hype' ? 640 : id === 'retro' || id === 'handmade' ? 600 : 680;
  // The Handmade mat is taller than its photo (the frame of `storyCoverFrame`).
  const height = coverFrameHeight(id, width, false);
  const shift = id === 'retro' ? -100 : 0;
  return {
    x: CANVAS_STAGE_CENTRE.x - width / 2 + shift,
    y: Math.round(CANVAS_STAGE_CENTRE.y - height / 2),
    w: width,
    h: height,
  };
}

export interface CanvasPose {
  /** The cover: the float and the kick (or the breath of Dreamy, the turn of Hype and Handmade). */
  cover: { y: number; scale: number; rotate: number };
  /** The theme motion: the ground drift (Clean, Dreamy), the grain and the sweep (Noir), the sun (Warm), the record (Retro), the glow (Neon). */
  theme: { driftX: number; driftY: number; grain: number; sweep: number; sun: number; record: number; glow: number };
}

/** The ease-out of the kick (cubic-bezier .22, 1, .36, 1 is close to a quint ease-out). */
const outQuint = (x: number): number => 1 - (1 - x) ** 5;

/** The kick of the cover: 3 % at its peak (design s `cvBeat`). */
export const CANVAS_KICK_SCALE = 0.03;
/** The attack of the kick: the cover is at 100 % on the downbeat frame and peaks this many frames later. */
export const CANVAS_KICK_ATTACK_FRAMES = 3;

const outCubic = (x: number): number => 1 - (1 - x) ** 3;
const smooth = (x: number): number => x * x * (3 - 2 * x);

/**
 * The kick at the share `inBar` of a bar (0..1) with `framesPerBar` frames: 0 on the downbeat frame,
 * up to 1 over `CANVAS_KICK_ATTACK_FRAMES` frames, back to 0 over 0.36 of a bar (design s `cvBeat`,
 * owner decision 2026-10-08: an attack, so the loop point is a frame at rest). Pure.
 */
export function canvasKick(inBar: number, framesPerBar: number): number {
  const attack = CANVAS_KICK_ATTACK_FRAMES / Math.max(CANVAS_KICK_ATTACK_FRAMES + 1, framesPerBar);
  if (inBar < attack) {
    return outCubic(inBar / attack);
  }
  const back = (inBar - attack) / 0.36;
  return back < 1 ? 1 - outQuint(back) : 0;
}

/**
 * The Hype sticker turn (design s `cvRock`): 7 degrees on the downbeats of the even bars and 3 on
 * the odd ones, back to 5 in 0.4 of a bar (ease-out), then on to the angle of the next downbeat; the
 * last bar turns back to 7, so the loop joins. Pure.
 */
export function hypeRock(inBar: number, barIndex: number, bars: number): number {
  const at = (index: number): number => (index % 2 === 0 ? 7 : 3);
  const start = at(barIndex);
  const next = barIndex + 1 >= bars ? at(0) : at(barIndex + 1);
  if (inBar < 0.4) {
    return start + (5 - start) * outQuint(inBar / 0.4);
  }
  return 5 + (next - 5) * smooth((inBar - 0.4) / 0.6);
}

/**
 * The Neon glow (design s `cvGlow`, 0..1): full on each downbeat, down to 0 in 0.48 of a bar and
 * back up to full by the next downbeat, both with an ease-out. Pure.
 */
export function neonGlow(inBar: number): number {
  return inBar < 0.48 ? 1 - outCubic(inBar / 0.48) : outCubic((inBar - 0.48) / 0.52);
}

/**
 * The pose of a Canvas at frame `frame` (design s, `CanvasFrame`). Every value is a function of
 * `frame / loopFrames` with that period, so frame `loopFrames` equals frame 0. Pure.
 */
export function canvasPose(
  themeId: string,
  video: Pick<KitCanvasVideo, 'bars' | 'loopFrames'>,
  frame: number
): CanvasPose {
  const id = storyThemeIdOf(themeId);
  const p = (((frame % video.loopFrames) + video.loopFrames) % video.loopFrames) / video.loopFrames;
  const bars = Math.max(1, video.bars);
  const inBar = (p * bars) % 1;
  const barIndex = Math.floor(p * bars + 1e-9);
  const kick = canvasKick(inBar, video.loopFrames / bars);
  const wave = Math.sin(2 * Math.PI * p);
  const breath = (1 - Math.cos(2 * Math.PI * p)) / 2;
  let scale = 1 + CANVAS_KICK_SCALE * kick;
  let rotate = 0;
  if (id === 'dreamy') {
    // Dreamy breathes over the loop and kicks on each downbeat too (owner decision 2026-10-08).
    scale = (1 + 0.025 * breath) * (1 + CANVAS_KICK_SCALE * kick);
  }
  if (id === 'hype') {
    rotate = hypeRock(inBar, barIndex, bars);
  }
  if (id === 'handmade') {
    rotate = -4 + 1.2 * wave;
  }
  // The Noir sweep: off the cover until 12 % of the loop, across it by 62 %, eased.
  const sweepT = Math.min(1, Math.max(0, (p - 0.12) / 0.5));
  const sweep = -160 + 320 * (0.5 - Math.cos(Math.PI * sweepT) / 2);
  return {
    cover: { y: round3(-14 * wave), scale: round3(scale), rotate: round3(rotate) },
    theme: {
      driftX: round3(36 * wave),
      driftY: round3(-24 * (1 - Math.cos(2 * Math.PI * p))),
      grain: Math.floor(p * 24) / 24,
      sweep: round3(sweep),
      sun: round3(breath),
      record: round3(360 * p),
      glow: round3(id === 'neon' ? neonGlow(inBar) : 0),
    },
  };
}

/** The colour that darkens the top bar and the player zone: the darker of the text and the ground. Pure. */
export function canvasShadeColour(colors: StoryColours): string {
  const luma = (hex: string): number => {
    const value = (index: number): number => {
      const parsed = parseInt(hex.slice(1 + index * 2, 3 + index * 2), 16);
      return Number.isNaN(parsed) ? 0 : parsed;
    };
    return 0.2126 * value(0) + 0.7152 * value(1) + 0.0722 * value(2);
  };
  return luma(colors.text) < luma(colors.ink) ? colors.text : mixHex(colors.ink, '#000000', 0.5);
}

/**
 * The shade over the ground (design s `.cv-shade`): 34 % at the top edge to 0 at y 260; 0 from the
 * player top; 40 % at y 1400 to 62 % at the bottom edge, so the white Spotify text reads. Pure.
 */
export function canvasShade(colors: StoryColours): string {
  const shade = canvasShadeColour(colors);
  return `linear-gradient(180deg, ${storyAlpha(shade, 0.34)} 0px, transparent 260px, transparent ${CANVAS_PLAYER_TOP}px, ${storyAlpha(shade, 0.4)} 1400px, ${storyAlpha(shade, 0.62)} 1920px)`;
}

/**
 * The still ornaments of a Canvas (design s `.cv-deco`): the Hype stripes (one under the top bar,
 * one between the cover and the song title), the Noir accent line under the cover, the Retro arcs
 * and the Handmade strip in the player zone. They never move. Pure.
 */
export function canvasDeco(themeId: string, colors: StoryColours): StoryCss[] {
  const id = storyThemeIdOf(themeId);
  const band = (style: StoryCss): StoryCss => ({
    position: 'absolute',
    left: '-20%',
    right: '-20%',
    transform: 'rotate(-6deg)',
    ...style,
  });
  const arc = (top: number, background: string): StoryCss => ({
    position: 'absolute',
    left: '-30%',
    right: '-30%',
    top,
    height: 1200,
    borderRadius: '50%',
    background,
  });
  switch (id) {
    case 'hype':
      return [
        band({ top: 236, height: 56, background: colors.surface }),
        band({ top: 1046, height: 70, background: colors.surface }),
        band({ top: 1150, height: 18, background: colors.accent }),
      ];
    case 'dark':
      return [{ position: 'absolute', left: 460, width: 160, top: 1050, height: 3, background: colors.accent }];
    case 'retro':
      return [arc(1500, mixHex(colors.text, colors.ink, 0.7)), arc(1590, colors.surface), arc(1680, colors.accent)];
    case 'handmade':
      return [
        {
          position: 'absolute',
          left: '-5%',
          right: '-5%',
          top: 1600,
          height: 170,
          background: colors.surface,
          transform: 'rotate(-3deg)',
          clipPath:
            'polygon(0 12%, 6% 0, 13% 10%, 21% 2%, 30% 12%, 39% 3%, 48% 11%, 57% 1%, 66% 10%, 75% 2%, 84% 12%, 93% 3%, 100% 9%, 100% 88%, 92% 100%, 83% 90%, 74% 99%, 65% 89%, 55% 98%, 46% 90%, 37% 100%, 28% 91%, 18% 99%, 9% 90%, 0 98%)',
        },
      ];
    case 'clean':
    case 'dreamy':
    case 'warm':
    case 'neon':
      return [];
    default: {
      const exhaustive: never = id;
      return exhaustive;
    }
  }
}

/** The ground of a Canvas (design s `.cv-ground`), over the background colour. Null: flat (Clean has the blurred cover). */
export function canvasGround(themeId: string, colors: StoryColours): string | null {
  const id = storyThemeIdOf(themeId);
  switch (id) {
    case 'hype':
      return `radial-gradient(ellipse 70% 30% at 50% 34%, ${storyAlpha(colors.accent, 0.25)}, transparent 70%)`;
    case 'dreamy':
      return `linear-gradient(180deg, ${colors.ink} 0%, ${colors.surface} 100%)`;
    case 'dark':
      return `radial-gradient(ellipse 60% 30% at 50% 34%, ${colors.surface}, transparent 75%)`;
    case 'warm':
      return `linear-gradient(180deg, ${colors.surface} 0%, ${colors.ink} 62%)`;
    case 'retro':
      return 'radial-gradient(rgba(255,255,255,.08) 1px, transparent 1.5px) 0 0 / 6px 6px';
    case 'neon':
      return `radial-gradient(ellipse 70% 25% at 15% 8%, ${storyAlpha(colors.text, 0.26)}, transparent 70%), radial-gradient(ellipse 80% 25% at 85% 70%, ${storyAlpha(colors.accent, 0.22)}, transparent 70%)`;
    case 'handmade':
      return 'radial-gradient(rgba(80,50,30,.05) 1px, transparent 1.6px) 0 0 / 9px 9px, radial-gradient(ellipse 90% 50% at 50% 34%, rgba(255,255,255,.5), transparent 75%)';
    case 'clean':
      return null;
    default: {
      const exhaustive: never = id;
      return exhaustive;
    }
  }
}
